import React from 'react';
import DataTable, { TableColumn, TableStyles } from 'react-data-table-component';
import './DataGrid.css';
import { DataGridProps, DataGridState, IColumnState, ISelectedDateRange, ITabData } from './Interface';
import DownloadCsv from './Plugins/DownloadCsv';
import Search from './Plugins/Search/Search';
import Popover from '@mui/material/Popover';
import { DateRangePicker } from 'react-date-range';
import BigButton from '../BigButton';
import IconButton from '@mui/material/IconButton';
import { DateRange, Refresh, Save } from '@mui/icons-material';
import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import { Tabs, Tab, Button, Box, Menu, MenuItem, ListItemIcon, Checkbox, ListItemText } from '@mui/material';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import SettingsApplicationsRoundedIcon from '@mui/icons-material/SettingsApplicationsRounded';
import FilterAltSharpIcon from '@mui/icons-material/FilterAltSharp';
import Badge from '@mui/material/Badge';
import { getLast30DaysRangeAsDate, Texts } from '../Reusables/Utility';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';

interface IDataRow {
    [column: string]: string | number | Date | any;
}

class DataGrid<D extends IDataRow, C> extends React.Component<DataGridProps<D>, DataGridState<D>> {
    // Define defaultProps as a static property
    static defaultProps: Partial<DataGridProps<any>> = {
        hasColumnSelection: true,
        useExternalTriggerForColumsMenu: false
    };
    private _gridId: string = '';
    constructor(props_: DataGridProps<D>) {
        super(props_);
        this.state = {
            isRowExpanded: false,
            selectedDateRange: getLast30DaysRangeAsDate(),
            popOverTargetElementForDateRange: null,
            filteredDate: { startDate: '', endDate: '' },
            lastSelectedDateRange: { startDate: null, endDate: null },
            isFilterIcon: false,
            eleTriggerColumnsMenu: this.props.eleTriggerColumnsMenuExternal ? this.props.eleTriggerColumnsMenuExternal : null,
            columns: this.props.columns
        }
    }

    componentDidMount(): void {
        this.restoreColumnState();
    }

    //To trigger element child compoennt if element in parent is changed
    componentDidUpdate(prevProps: DataGridProps<D>) {
        if (prevProps.eleTriggerColumnsMenuExternal !== this.props.eleTriggerColumnsMenuExternal) {
            this.setState({
                eleTriggerColumnsMenu: this.props.eleTriggerColumnsMenuExternal ? this.props.eleTriggerColumnsMenuExternal : null
            })
        }
        // to update columns on tab change
        if (prevProps.columns !== this.props.columns) {
            this.setState({
                columns: this.props.columns
            })
            this.restoreColumnState();
        }
    }

    private handleSelectDateRange = (ranges: any, updateValue: keyof DataGridState<D>) => {
        const selectedRange = ranges.range1;

        // Update the state dynamically based on the provided updateValue (key)
        this.setState((prevState) => ({
            ...prevState,
            [updateValue]: selectedRange,
        }));
    };

    private applyDateRange = (updateValue: keyof DataGridState<D>) => {
        this.setState((prevState) => {
            let newDate = prevState.selectedDateRange;
            //To handle null value, which is modified due to search box
            if (newDate.startDate === null) {
                newDate = this.state.lastSelectedDateRange;
            }
            const filteredDateString = this.getFilteredDate(newDate);
            // Return a complete state object
            return {
                ...prevState, // Spread the previous state to keep other properties
                [updateValue]: newDate, // Update the specific property
                filteredDate: filteredDateString,
                popOverTargetElementForDateRange: null
            };
        }, () => {
            // This callback ensures that we have the latest state after setState completes
            if (this.props.onApply) {
                this.setState({
                    lastSelectedDateRange: this.state.selectedDateRange
                })
                this.props.onApply(this.state.filteredDate); // Pass the latest filtered date
            }
        });
    };

    private getFilteredDate(newDate: ISelectedDateRange) {
        return {
            startDate: newDate.startDate ? `${moment(newDate.startDate).startOf('day').utc().format(Texts.formatDateTime)}` : '',
            endDate: newDate.endDate ? `${moment(newDate.endDate).endOf('day').utc().format(Texts.formatDateTime)}` : ''
        }
    }

    disableFilter() {
        this.setState({
            isFilterIcon: false,
            selectedDateRange: getLast30DaysRangeAsDate()
        });
        this.applyDateRange('selectedDateRange');
    }

    //Determine visible tab based on permissions and pages
    public getVisibleTabs(): ITabData[] {
        let lstTabs: ITabData[] = [];
        if (this.props.tabData) {
            lstTabs = this.props.tabData;
        }
        return lstTabs?.filter(tab => tab.visible) || [];
    }

    // Tab menu width for different pages
    public getDynamicWidth(visibleTabs: ITabData[]) {
        let totalWidth = 0;
        for (let tab of visibleTabs) {
            totalWidth += tab.width ? tab.width : 0;
        }
        return totalWidth;
    }

    //show columns menu
    public showColumnsMenu(event: React.MouseEvent<HTMLElement>) {
        this.setState({
            eleTriggerColumnsMenu: event.currentTarget
        })
    }

    //hide columns menu
    public closeColumnsMenu() {
        this.setState({
            eleTriggerColumnsMenu: null
        })
        this.props.onCloseColumnsMenuExternal && this.props.onCloseColumnsMenuExternal();
    }

    //hide columns menu
    public showHideColumn(col: TableColumn<D>) {
        col.omit = !col.omit;
        this.setState(prev => ({
            columns: prev.columns.map(colPrev =>
                colPrev.id === col.id ? { ...colPrev, omit: col.omit } : colPrev
            )
        }));
        this.saveColumnState();
    }

    public render() {
        const visibleTabs = this.getVisibleTabs();
        const dynamicWidth = this.getDynamicWidth(visibleTabs);
        const dateValue = this.state.selectedDateRange.startDate ? `${this.state.selectedDateRange.startDate?.toLocaleDateString()} - ${this.state.selectedDateRange.endDate?.toLocaleDateString()}` : '';
        const selector = `&[data-column-id='#colId']>div>div`;
        let cssColsAlignRight = '';
        let cssColsAlignCenter = '';
        let cssColsPaddingLeft = '';
        for (let col of this.props.columns) {
            if (col.id) {
                let selectorCol = selector.replace('#colId', col.id.toString())
                if (col.right) {
                    if (cssColsAlignRight !== '') cssColsAlignRight += ',';
                    cssColsAlignRight += selectorCol;
                }
                if (col.center) {
                    if (cssColsAlignCenter !== '') {
                        cssColsAlignCenter += ',';
                        cssColsPaddingLeft += ',';
                    }
                    cssColsAlignCenter += selectorCol;
                    cssColsPaddingLeft += selectorCol.replace(new RegExp('>div$'), '')
                }
            }
        }
        /* To right align header text for right aligned columns */
        let styleAlignRight = {};
        if (cssColsAlignRight !== '') {
            styleAlignRight = JSON.parse(`{
            "${cssColsAlignRight}": {
                "textAlign": "right"
            }
        }`);
        }
        /* To center align header text for center aligned columns */
        let styleAlignCenter = {};
        if (cssColsAlignCenter !== '') {
            styleAlignCenter = JSON.parse(`{
            "${cssColsAlignCenter}": {
                "textAlign": "center"
            },
            "${cssColsPaddingLeft}": {
                "paddingLeft": "20px"
            }
        }`);
        }

        const customStyles: TableStyles = {
            // headRow: {
            //     style: {
            //        //...
            //     },
            // },
            headCells: {
                style: {
                    paddingLeft: '10px',
                    paddingRight: '10px',
                    ...styleAlignRight,
                    ...styleAlignCenter
                }
            },
            cells: {
                style: {
                    paddingLeft: '10px',
                    paddingRight: '10px',
                },
            },
        };
        return (
            <>
                {(this.props.title || this.props.isDatePickerNeeded || this.props.search || this.props.exportCsv) && <div className='dt-table-header-container'>
                    {
                        this.props.title && <h1>{this.props.title}</h1>
                    }
                    {<div className='dt-table-header'>{

                        this.props.isDatePickerNeeded && <div className='calendar-table-header'>
                            <span style={{ color: '#817e9e', fontWeight: 'bold', padding: '8px', minWidth: '175px' }}>{dateValue}</span>
                            <IconButton
                                data-testid="date-picker-apply"
                                onClick={(event) => {
                                    this.setState({
                                        popOverTargetElementForDateRange: event.target as HTMLElement
                                    });
                                }
                                }
                            >
                                <DateRange />
                            </IconButton>
                            {this.state.isFilterIcon && <span><IconButton><Tooltip title="Filter date">
                                <Refresh
                                    onClick={(event) => {
                                        this.disableFilter()
                                    }
                                    }></Refresh></Tooltip></IconButton> </span>}
                        </div>
                    }
                        <Popover
                            id={'date-range-popover'}
                            open={Boolean(this.state.popOverTargetElementForDateRange)}
                            anchorEl={this.state.popOverTargetElementForDateRange}
                            onClose={() =>
                                this.setState({ popOverTargetElementForDateRange: null })
                            }
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                        >
                            <div className="popover-calendar">
                                <DateRangePicker
                                    ranges={[this.state.selectedDateRange as any]}
                                    onChange={(ranges: any) => {
                                        this.handleSelectDateRange(ranges, 'selectedDateRange');
                                    }}
                                />
                                <BigButton
                                    disabled={false}
                                    width="80%"
                                    onClick={() => {
                                        this.setState({
                                            isFilterIcon: true
                                        })
                                        this.applyDateRange('selectedDateRange');
                                    }}
                                    buttonText="Apply"
                                />
                            </div>
                        </Popover>
                        {
                            this.props.search && <Search
                                onSearch={(searchData: string) => {
                                    let bSendDateAsParam = false;
                                    if (this.props.isClearDateOnSearch) {
                                        let newStartDate: Date | null = null;
                                        let newEndDate: Date | null = null;
                                        if (searchData.trim() !== '') {
                                            if (this.state.selectedDateRange.startDate !== null) {
                                                this.setState({ lastSelectedDateRange: this.state.selectedDateRange });
                                            }
                                        }
                                        else {
                                            newStartDate = this.state.lastSelectedDateRange.startDate;
                                            newEndDate = this.state.lastSelectedDateRange.endDate;
                                            bSendDateAsParam = true;
                                        }
                                        this.setState(() => {
                                            return { selectedDateRange: { startDate: newStartDate, endDate: newEndDate } }
                                        }, () => {
                                            if (this.props.onSearch) {
                                                this.props.onSearch(searchData, bSendDateAsParam ? this.getFilteredDate(this.state.selectedDateRange) : null);
                                            }
                                        });
                                    }
                                    else if (this.props.onSearch) {
                                        this.props.onSearch(searchData, null);
                                    }
                                }}
                                searchPlaceholder={this.props.search}
                                defaultValue={this.props.defaultSearch}
                            />
                        }
                        {this.props.hasColumnSelection && (
                            <span>
                                {!this.props.useExternalTriggerForColumsMenu && (<Tooltip title="Show/hide Columns" arrow>
                                    <Button
                                        color="primary"
                                        onClick={this.showColumnsMenu.bind(this)}
                                    >
                                        <ViewColumnIcon color="secondary" fontSize="large" />
                                    </Button>
                                </Tooltip>
                                )}
                                <Menu
                                    className="columns-menu"
                                    anchorEl={this.state.eleTriggerColumnsMenu}
                                    elevation={3}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right'
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right'
                                    }}
                                    sx={{
                                        marginTop: '5px',
                                        marginLeft: '-10px'
                                    }}
                                    open={Boolean(this.state.eleTriggerColumnsMenu)}
                                    onClose={this.closeColumnsMenu.bind(this)}
                                >
                                    {
                                        this.props.columns.map((col, index) => {
                                            if (col.id) {
                                                return <MenuItem key={col.id} onClick={() => { this.showHideColumn(col) }}>
                                                    <ListItemIcon>
                                                        <Checkbox
                                                            edge="start"
                                                            checked={!col.omit}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            color='secondary'
                                                            sx={{
                                                                padding: '1px',
                                                                paddingLeft: '10px'
                                                            }}
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText primary={col.name} className='label-column-menu' />
                                                </MenuItem>
                                            }
                                            return undefined;
                                        })
                                    }
                                </Menu></span>
                        )}
                        {
                            this.props.exportCsv && <DownloadCsv data={this.props.csvData ?? this.props.data} columns={this.props.columns} selectedDate={this.state.selectedDateRange} csvFileName={this.props.csvFileName} isRestrictExportFor31Days={this.props.isRestrictExportFor31Days} prepareCSVCustom={this.props.prepareCSVCustom} parentComponent={this.props.parentComponent} />
                        }
                        {this.props.showSaveButton && (
                            <Tooltip title={this.props.titleSaveButton} arrow>
                                <Button
                                    color="primary"
                                    style={{ minWidth: 'unset' }}
                                    onClick={this.props.onClickSaveButton}
                                >
                                    <Save color="secondary" fontSize="large" />
                                </Button>
                            </Tooltip>
                        )}

                        {this.props.showAddButton && (
                            <Tooltip title={this.props.titleAddButton} arrow>
                                <Button
                                    color="primary"
                                    style={{ minWidth: 'unset' }}
                                    onClick={this.props.onClickAddButton}
                                >
                                    <AddCircleSharpIcon color="secondary" fontSize="large" />
                                </Button>
                            </Tooltip>
                        )}
                        {this.props.showAddUserButton && (
                            <Tooltip title={this.props.titleAddUserButton} arrow>
                                <Button
                                    color="primary"
                                    onClick={this.props.onClickAddUserButton}
                                >
                                    <PersonAddAltRoundedIcon color="secondary" fontSize="large" />
                                </Button>
                            </Tooltip>
                        )}
                        {this.props.showSettingsButton && (
                            <Tooltip title={this.props.titleSettingsButton} arrow>
                                <Button
                                    color="primary"
                                    onClick={this.props.onClickSettingsButton}
                                >
                                    <SettingsApplicationsRoundedIcon color="secondary" fontSize="large" />
                                </Button>
                            </Tooltip>
                        )}
                        {this.props.isDrawerRequired && (
                            <Tooltip title="Open Filters" arrow>
                                <Button
                                    style={{ width: '55px', marginLeft: '-10px' }}
                                    onClick={this.props.isDrawerOpen}
                                >
                                    <Badge
                                        badgeContent={this.props.appliedFiltersCount}
                                        color="warning"
                                        invisible={this.props.appliedFiltersCount === 0} // hide badge when no filter
                                    >

                                        <FilterAltSharpIcon fontSize="large" color="secondary" />

                                    </Badge>
                                </Button>
                            </Tooltip>
                        )}
                    </div>
                    }
                </div>}
                {this.props.onTabChange && visibleTabs.length > 0 && (
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: dynamicWidth, // Dynamic width based on tab count
                            bgcolor: "background.paper",
                            borderTopLeftRadius: "6px",
                            borderTopRightRadius: "6px",
                        }}
                    >
                        <Tabs
                            value={this.props.selectedTab}
                            onChange={this.props.onTabChange}
                            centered
                            indicatorColor="secondary"
                            textColor="primary"
                            sx={{ borderBottom: 1, borderColor: "divider" }}>
                            {visibleTabs.map((tab, index) => {
                                let style = {};
                                if (tab.width) {
                                    style = { width: `${tab.width}px` }
                                }
                                return <Tab key={tab.label} label={tab.label} style={style} value={tab.value} data-testid={`tab-${index}`} />
                            })}
                        </Tabs>
                    </Box>
                )}
                <DataTable
                    customStyles={customStyles}
                    dense={this.props.dense}
                    className='passport-datagrid'
                    fixedHeader
                    columns={this.state.columns}
                    pagination={this.props.pagination}
                    highlightOnHover
                    pointerOnHover
                    expandOnRowClicked
                    expandableRows={this.props.expandableRows}
                    expandableRowsComponent={
                        this.props.expandedComponent
                    }
                    onRowExpandToggled={(expanded: boolean) => {
                        this.setState({
                            isRowExpanded: expanded
                        })
                        // console.log(expanded);
                    }}
                    paginationServer
                    paginationTotalRows={this.props.paginationTotalRows}
                    onChangeRowsPerPage={this.props.onChangeRowsPerPage}
                    onChangePage={this.props.onChangePage}
                    data={this.props.data}
                    paginationDefaultPage={this.props.paginationDefaultPage}
                    paginationResetDefaultPage={this.props.paginationResetDefaultPage}
                />
            </>
        )
    }

    //retrieve the saved state of a grid
    private restoreColumnState() {
        this.setGridId();
        if (this.props.hasColumnSelection && localStorage[this._gridId]) {
            let columnState: Record<string, IColumnState> = JSON.parse(localStorage[this._gridId]);
            this.setState(prev => ({
                columns: prev.columns.map(colPrev => {
                    if (colPrev.id && columnState[colPrev.id]) {
                        let col = this.props.columns.find(c => colPrev.id === c.id) as TableColumn<D>;
                        col.omit = columnState[colPrev.id].omit;// to update value in columns value
                        return { ...colPrev, omit: columnState[colPrev.id].omit };// to update data grid
                    }
                    return colPrev;
                })
            }));
        }
    }

    //save the grid columns' stae
    private saveColumnState() {
        let columnState: Record<string, IColumnState> = {};
        for (let col of this.props.columns) {
            if (col.id) {
                columnState[col.id] = {
                    omit: col.omit
                }
            }
        }
        localStorage[this._gridId] = JSON.stringify(columnState);
    }

    private setGridId() {
        this._gridId = `DataGrid_${this.props.gridId ? this.props.gridId : this.props.title ? this.props.title.replaceAll(' ', '') : '1'}${this.props.selectedTab !== null && this.props.selectedTab !== undefined ? `_${this.props.selectedTab}` : ''}`;
    }

}

export const renderTooltipCell = (data: any) => {
    const value = data ? data.toString() : "";
    return <span
        title={value}
        style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
        }}>
        {value}
    </span>;
};

export default DataGrid;